import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/authFetch';
import { zh } from '../../utils/zh';

interface Thread {
    id: string;
    direction: 'inbound' | 'outbound';
    fromEmail: string;
    toEmail: string;
    subject: string;
    bodyText: string;
    receivedAt: string;
    isRead: boolean;
    leadId?: string;
}

interface Lead {
    id: string;
    name: string;
    companyName: string;
    email: string;
    title: string;
    country: string;
    status: string;
}

type FilterStatus = 'all' | 'unread' | 'inbound' | 'outbound';

export function InboxPage() {
    const [threads, setThreads] = useState<Thread[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [filter, setFilter] = useState<FilterStatus>('all');
    const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
    const [selectedLeadThreads, setSelectedLeadThreads] = useState<Thread[]>([]);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);

    const loadInbox = useCallback(async () => {
        try {
            const res = await authFetch('/replies/unread');
            const data = await res.json();
            setThreads(data.threads || []);
        } catch { }
    }, []);

    const loadLeads = useCallback(async () => {
        try {
            const res = await authFetch('/campaigns');
            const data = await res.json();
            const allLeads: Lead[] = [];
            for (const c of data.campaigns || []) {
                try {
                    const lr = await authFetch(`/campaigns/${c.id}`);
                    const ld = await lr.json();
                    allLeads.push(...(ld.leads || []));
                } catch { }
            }
            setLeads(allLeads);
        } catch { }
    }, []);

    useEffect(() => {
        loadInbox();
        loadLeads();
        const t = setInterval(loadInbox, 30000);
        return () => clearInterval(t);
    }, [loadInbox, loadLeads]);

    const loadLeadThreads = async (leadId: string) => {
        try {
            const res = await authFetch(`/leads/${leadId}/threads`);
            const data = await res.json();
            setSelectedLeadThreads(data.threads || []);
        } catch { }
    };

    const filteredLeads = leads.filter(l => {
        if (filter === 'all') return true;
        if (filter === 'unread') return l.status === 'replied';
        return true;
    });

    const sendReply = async () => {
        if (!replyText.trim() || !selectedThread) return;
        setSending(true);
        try {
            await authFetch('/reply', {
                method: 'POST',
                body: JSON.stringify({
                    leadId: selectedThread.leadId,
                    toEmail: selectedThread.fromEmail,
                    fromEmail: selectedThread.toEmail,
                    subject: `Re: ${selectedThread.subject}`,
                    bodyText: replyText,
                    inReplyToMessageId: selectedThread.id,
                }),
            });
            setReplyText('');
            if (selectedThread.leadId) loadLeadThreads(selectedThread.leadId);
        } catch (e) { alert(String(e)); }
        finally { setSending(false); }
    };

    return (
        <div style={{ height: 'calc(100vh - var(--header-height) - 48px)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h2 className="page-title">📥 收件箱</h2>
                    <p className="page-subtitle">查看所有账号的邮件往来</p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {(['all', 'unread', 'inbound', 'outbound'] as FilterStatus[]).map(f => (
                        <button key={f} className={`filter-chip${filter === f ? ' active' : ''}`}
                            onClick={() => setFilter(f)}>
                            {zh(f)}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '360px 1fr', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border-default)' }}>
                {/* Left: email list */}
                <div className="split-left" style={{ background: 'var(--bg-surface)' }}>
                    {filteredLeads.length === 0 && threads.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📭</div>
                            <p>暂无邮件</p>
                            <p style={{ fontSize: 12 }}>创建获客活动，开始发现联系人</p>
                        </div>
                    ) : (
                        <>
                            {threads.map((t) => (
                                <div
                                    key={t.id}
                                    className={`mail-item${selectedThread?.id === t.id ? ' active' : ''}${!t.isRead ? ' unread' : ''}`}
                                    onClick={() => {
                                        setSelectedThread(t);
                                        if (t.leadId) loadLeadThreads(t.leadId);
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span className="mail-from">
                                            {t.direction === 'inbound' ? t.fromEmail : t.toEmail}
                                        </span>
                                        <span className="mail-time">{new Date(t.receivedAt).toLocaleDateString()}</span>
                                    </div>
                                    <span className="mail-subject">{t.subject}</span>
                                    <span className="mail-preview">{t.bodyText.slice(0, 80)}</span>
                                </div>
                            ))}
                            {filteredLeads.filter(l => l.status === 'replied').map((l) => (
                                <div
                                    key={l.id}
                                    className={`mail-item unread`}
                                    onClick={() => {
                                        loadLeadThreads(l.id);
                                        setSelectedThread(null);
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span className="mail-from">{l.name}</span>
                                        <span className="mail-time">{l.country}</span>
                                    </div>
                                    <span className="mail-subject">{l.title} @ {l.companyName}</span>
                                    <span className="mail-preview">{l.email}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Right: thread view */}
                <div className="split-right" style={{ background: 'var(--bg-base)', display: 'flex', flexDirection: 'column' }}>
                    {selectedLeadThreads.length === 0 && !selectedThread ? (
                        <div className="empty-state" style={{ flex: 1 }}>
                            <div className="empty-icon">💬</div>
                            <p>选择一个邮件会话</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ flex: 1, padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {(selectedLeadThreads.length > 0 ? selectedLeadThreads : (selectedThread ? [selectedThread] : [])).map((t) => (
                                    <div key={t.id} style={{
                                        alignSelf: t.direction === 'outbound' ? 'flex-end' : 'flex-start',
                                        maxWidth: '80%',
                                        background: t.direction === 'outbound' ? 'var(--accent-bg)' : 'var(--bg-elevated)',
                                        padding: '12px 16px',
                                        borderRadius: 'var(--radius-lg)',
                                    }}>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                                            {t.direction === 'outbound' ? `我 → ${t.toEmail}` : `${t.fromEmail} → You`}
                                            {' · '}{new Date(t.receivedAt).toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                                            {t.bodyText}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Reply box */}
                            <div style={{ padding: 16, borderTop: '1px solid var(--border-default)', display: 'flex', gap: 8 }}>
                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="输入回复内容…"
                                    className="input"
                                    rows={3}
                                    style={{ flex: 1 }}
                                />
                                <button
                                    className="btn btn-success"
                                    onClick={sendReply}
                                    disabled={sending || !replyText.trim()}
                                    style={{ alignSelf: 'flex-end' }}
                                >
                                    {sending ? '...' : "📤 发送"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
